/**
 * Map chrome: global search, the advanced filter popover, and the view toggles.
 * The page owns filter state; this component only renders and emits changes.
 */

import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
    Search, SlidersHorizontal, X, Flame, Layers, Tag, Navigation,
    LayoutGrid, RotateCcw, Check, Footprints,
} from 'lucide-react';
import { UNITS, CATEGORIES, CATEGORY_COLORS, STATUS_COLORS } from '../data/mallData';
import { formatMoney, formatNumber } from '../../Shared/Formatters';

const STATUSES = ['Occupied', 'Vacant', 'Coming Soon', 'Under Renovation'];

export const DEFAULT_FILTERS = {
    categories: [],
    statuses: [],
    floorsOnly: false,       // restrict search results to the active floor
    vacantOnly: false,
    highPerformers: false,   // top quartile vs category
    expiringSoon: false,     // <= 6 months
    atRisk: false,
    rentMax: null,
    salesMin: null,
};

export function filtersActive(f) {
    return f.categories.length > 0 || f.statuses.length > 0 || f.vacantOnly || f.highPerformers
        || f.expiringSoon || f.atRisk || f.rentMax !== null || f.salesMin !== null;
}

/** Apply the filter set to the full unit list. Returns the surviving units. */
export function applyFilters(units, f) {
    return units.filter((u) => {
        if (f.categories.length && !f.categories.includes(u.category)) return false;
        if (f.statuses.length && !f.statuses.includes(u.status)) return false;
        if (f.vacantOnly && u.status !== 'Vacant') return false;
        if (f.expiringSoon && !(u.lease && u.lease.remainingMonths <= 6)) return false;
        if (f.atRisk && u.riskLabel !== 'High') return false;
        if (f.highPerformers && !(u.performance && u.performance.vsCategoryPct >= 15)) return false;
        if (f.rentMax !== null && !(u.lease && u.lease.rentPerSqm <= f.rentMax)) return false;
        if (f.salesMin !== null && !(u.performance && u.performance.salesPerSqm >= f.salesMin)) return false;
        return true;
    });
}

const RENT_BOUNDS = (() => {
    const vals = UNITS.filter((u) => u.lease).map((u) => u.lease.rentPerSqm);
    return { min: Math.min(...vals), max: Math.max(...vals) };
})();

const SALES_BOUNDS = (() => {
    const vals = UNITS.filter((u) => u.performance).map((u) => u.performance.salesPerSqm);
    return { min: Math.min(...vals), max: Math.max(...vals) };
})();

function Toggle({ active, onClick, icon, children, title }) {
    return (
        <button
            type="button"
            className={`mm-toggle${active ? ' is-active' : ''}`}
            onClick={onClick}
            title={title}
            aria-pressed={active}
        >
            {icon}<span>{children}</span>
        </button>
    );
}

function SearchResults({ query, floor, restrictFloor, onPick }) {
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q.length < 1) return [];
        const pool = restrictFloor ? UNITS.filter((u) => u.floor === floor) : UNITS;
        return pool
            .map((u) => {
                const name = u.name.toLowerCase();
                const code = u.code.toLowerCase();
                const cat = u.category.toLowerCase();
                let score = -1;
                if (name.startsWith(q)) score = 0;
                else if (code.startsWith(q) || code.replace('-', '').startsWith(q.replace('-', ''))) score = 1;
                else if (name.includes(q)) score = 2;
                else if (cat.includes(q)) score = 3;
                else if (u.zoneName.toLowerCase().includes(q)) score = 4;
                return { u, score };
            })
            .filter((r) => r.score >= 0)
            .sort((a, b) => a.score - b.score || a.u.name.localeCompare(b.u.name))
            .slice(0, 12);
    }, [query, floor, restrictFloor]);

    if (!results.length) {
        return query.trim() ? <div className="mm-search-empty">No unit matches “{query}”.</div> : null;
    }

    return (
        <ul className="mm-search-results">
            {results.map(({ u }) => (
                <li key={u.id}>
                    <button type="button" onClick={() => onPick(u)}>
                        <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[u.category] }} />
                        <span className="mm-search-name">{u.name}</span>
                        <span className="mm-search-meta">{u.code} · {u.floorName} · {u.category}</span>
                        <span className="mm-search-status" style={{ color: STATUS_COLORS[u.status] }}>{u.status}</span>
                    </button>
                </li>
            ))}
        </ul>
    );
}

export default function MapToolbar({
    floor, filters, onFilters, onPickUnit,
    heatmap, onHeatmap,
    stackMode, onStackMode,
    labels, onLabels,
    walkableAvailable, showWalkable, onShowWalkable,
    directionsOpen, onDirections,
    dashboardOpen, onDashboard,
    onResetView,
    resultCount,
}) {
    const [query, setQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const searchRef = useRef(null);
    const filterRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
            if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const set = (patch) => onFilters({ ...filters, ...patch });

    const toggleIn = (list, value) => (
        list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
    );

    const active = filtersActive(filters);

    return (
        <div className="mm-toolbar">
            <div className="mm-search" ref={searchRef}>
                <Search size={16} />
                <input
                    value={query}
                    placeholder="Search stores, categories, unit codes…"
                    onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
                    onFocus={() => setSearchOpen(true)}
                    aria-label="Search the mall"
                />
                {query && (
                    <button type="button" className="mm-search-clear" onClick={() => { setQuery(''); setSearchOpen(false); }} aria-label="Clear search">
                        <X size={14} />
                    </button>
                )}
                {searchOpen && (
                    <SearchResults
                        query={query}
                        floor={floor}
                        restrictFloor={filters.floorsOnly}
                        onPick={(u) => { onPickUnit(u); setSearchOpen(false); setQuery(''); }}
                    />
                )}
            </div>

            <div className="mm-filter-wrap" ref={filterRef}>
                <button
                    type="button"
                    className={`mm-toggle${active ? ' is-active' : ''}`}
                    onClick={() => setFilterOpen((v) => !v)}
                    aria-expanded={filterOpen}
                >
                    <SlidersHorizontal size={15} />
                    <span>Filters</span>
                    {active && <em className="mm-filter-badge">{resultCount}</em>}
                </button>

                {filterOpen && (
                    <div className="mm-filter-pop">
                        <div className="mm-filter-sect">
                            <h5>Quick filters</h5>
                            <div className="mm-chiprow">
                                <button type="button" className={`mm-chip${filters.vacantOnly ? ' is-on' : ''}`} onClick={() => set({ vacantOnly: !filters.vacantOnly })}>Vacant only</button>
                                <button type="button" className={`mm-chip${filters.highPerformers ? ' is-on' : ''}`} onClick={() => set({ highPerformers: !filters.highPerformers })}>High performers</button>
                                <button type="button" className={`mm-chip${filters.expiringSoon ? ' is-on' : ''}`} onClick={() => set({ expiringSoon: !filters.expiringSoon })}>Expiring ≤ 6m</button>
                                <button type="button" className={`mm-chip${filters.atRisk ? ' is-on' : ''}`} onClick={() => set({ atRisk: !filters.atRisk })}>High risk</button>
                                <button type="button" className={`mm-chip${filters.floorsOnly ? ' is-on' : ''}`} onClick={() => set({ floorsOnly: !filters.floorsOnly })}>Search this floor only</button>
                            </div>
                        </div>

                        <div className="mm-filter-sect">
                            <h5>Category</h5>
                            <div className="mm-chiprow">
                                {CATEGORIES.map((c) => {
                                    const on = filters.categories.includes(c);
                                    return (
                                        <button
                                            key={c}
                                            type="button"
                                            className={`mm-chip${on ? ' is-on' : ''}`}
                                            style={on ? { background: `${CATEGORY_COLORS[c]}28`, borderColor: `${CATEGORY_COLORS[c]}88`, color: CATEGORY_COLORS[c] } : undefined}
                                            onClick={() => set({ categories: toggleIn(filters.categories, c) })}
                                        >
                                            {on && <Check size={11} />}
                                            <span className="mm-cat-dot" style={{ background: CATEGORY_COLORS[c] }} />
                                            {c}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mm-filter-sect">
                            <h5>Tenant status</h5>
                            <div className="mm-chiprow">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        className={`mm-chip${filters.statuses.includes(s) ? ' is-on' : ''}`}
                                        onClick={() => set({ statuses: toggleIn(filters.statuses, s) })}
                                    >
                                        <span className="mm-cat-dot" style={{ background: STATUS_COLORS[s] }} />
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mm-filter-sect">
                            <h5>Rent per m² — at most {filters.rentMax === null ? 'any' : formatMoney(filters.rentMax)}</h5>
                            <input
                                type="range"
                                min={RENT_BOUNDS.min}
                                max={RENT_BOUNDS.max}
                                step={50}
                                value={filters.rentMax === null ? RENT_BOUNDS.max : filters.rentMax}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    set({ rentMax: v >= RENT_BOUNDS.max ? null : v });
                                }}
                            />
                            <div className="mm-range-ends">
                                <span>{formatMoney(RENT_BOUNDS.min)}</span>
                                <span>{formatMoney(RENT_BOUNDS.max)}</span>
                            </div>
                        </div>

                        <div className="mm-filter-sect">
                            <h5>Sales per m² — at least {filters.salesMin === null ? 'any' : formatMoney(filters.salesMin)}</h5>
                            <input
                                type="range"
                                min={SALES_BOUNDS.min}
                                max={SALES_BOUNDS.max}
                                step={250}
                                value={filters.salesMin === null ? SALES_BOUNDS.min : filters.salesMin}
                                onChange={(e) => {
                                    const v = Number(e.target.value);
                                    set({ salesMin: v <= SALES_BOUNDS.min ? null : v });
                                }}
                            />
                            <div className="mm-range-ends">
                                <span>{formatMoney(SALES_BOUNDS.min)}</span>
                                <span>{formatMoney(SALES_BOUNDS.max)}</span>
                            </div>
                        </div>

                        <footer className="mm-filter-foot">
                            <span>{formatNumber(resultCount)} unit{resultCount === 1 ? '' : 's'} match</span>
                            <button type="button" className="mm-btn is-ghost" onClick={() => onFilters({ ...DEFAULT_FILTERS })}>
                                <RotateCcw size={13} /> Reset
                            </button>
                        </footer>
                    </div>
                )}
            </div>

            <div className="mm-toolbar-divider" />

            <div className="mm-heatgroup">
                <Flame size={15} />
                {[
                    { id: 'none', label: 'Off' },
                    { id: 'footfall', label: 'Footfall' },
                    { id: 'sales', label: 'Sales' },
                    { id: 'risk', label: 'Risk' },
                ].map((h) => (
                    <button
                        key={h.id}
                        type="button"
                        className={`mm-seg${heatmap === h.id ? ' is-active' : ''}`}
                        onClick={() => onHeatmap(h.id)}
                    >
                        {h.label}
                    </button>
                ))}
            </div>

            <div className="mm-toolbar-divider" />

            <Toggle active={stackMode} onClick={() => onStackMode(!stackMode)} icon={<Layers size={15} />} title="Show every level at once">Stack</Toggle>
            <Toggle active={labels} onClick={() => onLabels(!labels)} icon={<Tag size={15} />} title="Toggle store labels">Labels</Toggle>
            {/*
              * Only offered when a plan actually carries a navigable model. The
              * sample estate's corridors are declared geometry rather than a
              * traced grid, so there is nothing to draw and a toggle that did
              * nothing would read as a broken feature.
              */}
            {walkableAvailable && (
                <Toggle
                    active={showWalkable}
                    onClick={() => onShowWalkable(!showWalkable)}
                    icon={<Footprints size={15} />}
                    title="Show the walkable model routes are built from"
                >
                    Walkable
                </Toggle>
            )}
            <Toggle active={directionsOpen} onClick={onDirections} icon={<Navigation size={15} />} title="Wayfinding">Directions</Toggle>
            <Toggle active={dashboardOpen} onClick={onDashboard} icon={<LayoutGrid size={15} />} title="Management dashboard">Dashboard</Toggle>

            <button type="button" className="mm-toggle" onClick={onResetView} title="Reset the camera">
                <RotateCcw size={15} /><span>Reset</span>
            </button>
        </div>
    );
}
